import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';

// layout 
import AppLayout from '../../../layouts/AppLayout/AppLayout';

// components 
import { NioButton, NioToaster, NioCard, NioField, NioSection, NioBadge, NioIcon } from '../../../components';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

const DAY_KEYS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
const defaultDaySchedule = { open: false, start: '09:00', end: '18:00' };
const createDefaultWorkSchedule = () =>
  DAY_KEYS.reduce((acc, key) => ({ ...acc, [key]: { ...defaultDaySchedule } }), {});

export default function ProviderSignup() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const [searchParams] = useSearchParams();
  const isFirstProvider = searchParams.get('first') === '1';
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [service, setService] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [availableCities, setAvailableCities] = useState([]);
  const [userType, setUserType] = useState('individual');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    lowercase: false,
    uppercase: false,
    digit: false,
    length: false
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [cinPhotoFile, setCinPhotoFile] = useState(null);

  const days = useMemo(
    () => DAY_KEYS.map((key) => ({ key, label: t(`providerSignup.days.${key}`) })),
    [t],
  );

  const [workSchedule, setWorkSchedule] = useState(createDefaultWorkSchedule);

  // Tunisian regions (governorates) and their cities
  const tunisianRegions = {
    'Ariana': ['Ariana', 'Raoued', 'Kalâat el-Andalous', 'Sidi Thabet', 'Ettadhamen', 'Mnihla'],
    'Béja': ['Béja', 'Medjez el-Bab', 'Testour', 'Nefza', 'Amdoun', 'Téboursouk'],
    'Ben Arous': ['Ben Arous', 'Rades', 'Mégrine', 'Hammam Lif', 'Fouchana', 'Ezzahra', 'Mohamedia'],
    'Bizerte': ['Bizerte', 'Menzel Bourguiba', 'Mateur', 'Sejnane', 'Ghar El Melh', 'Ras Jebel'],
    'Gabès': ['Gabès', 'Médenine', 'Matmata', 'El Hamma', 'Ghannouch', 'Mareth'],
    'Gafsa': ['Gafsa', 'El Ksar', 'Métlaoui', 'Redeyef', 'Moularès', 'Sidi Aïch'],
    'Jendouba': ['Jendouba', 'Tabarka', 'Aïn Draham', 'Fernana', 'Bou Salem', 'Ghardimaou'],
    'Kairouan': ['Kairouan', 'Sbikha', 'Haffouz', 'Chebika', 'Oueslatia', 'Nasrallah'],
    'Kasserine': ['Kasserine', 'Sbeitla', 'Fériana', 'Thala', 'Haïdra', 'Jedelienne'],
    'Kébili': ['Kébili', 'Douz', 'Souk Lahad', 'Faouar', 'El Golâa'],
    'Kef': ['Le Kef', 'Tajerouine', 'Kalâat Khasba', 'Dahmani', 'Sers', 'Nebeur'],
    'Mahdia': ['Mahdia', 'Bou Merdes', 'Chebba', 'El Jem', 'Ksour Essef', 'Sidi Alouane'],
    'Manouba': ['Manouba', 'Douar Hicher', 'Oued Ellil', 'Mornag', 'Borj El Amri', 'Djedeida'],
    'Médenine': ['Médenine', 'Tataouine', 'Ben Gardane', 'Zarzis', 'Djerba', 'Houmt Souk'],
    'Monastir': ['Monastir', 'Moknine', 'Ksibet el-Médiouni', 'Bembla', 'Téboulba', 'Sayada'],
    'Nabeul': ['Nabeul', 'Hammamet', 'Kélibia', 'Korba', 'Menzel Temime', 'Dar Chaabane', 'Béni Khalled'],
    'Sfax': ['Sfax', 'Sakiet Ezzit', 'El Hencha', 'Agareb', 'Menzel Chaker', 'Bir Ali Ben Khalifa'],
    'Sidi Bouzid': ['Sidi Bouzid', 'Meknassy', 'Regueb', 'Ouled Haffouz', 'Cebbala Ouled Asker'],
    'Siliana': ['Siliana', 'Bou Arada', 'Gaâfour', 'El Krib', 'Makthar', 'Rouhia'],
    'Sousse': ['Sousse', 'Hammam Sousse', 'Akouda', 'Enfidha', 'Kalaa Kebira', 'Msaken', 'Bouficha'],
    'Tataouine': ['Tataouine', 'Remada', 'Bir Lahmar', 'Ghomrassen', 'Dehiba'],
    'Tozeur': ['Tozeur', 'Nefta', 'Degache', 'Hazoua', 'Tamaghza'],
    'Tunis': ['Tunis', 'La Marsa', 'Carthage', 'Sidi Bou Said', 'Le Bardo', 'La Goulette'],
    'Zaghouan': ['Zaghouan', 'Zriba', 'Nadhour', 'Fahs', 'Saouaf', 'El Fahs']
  };

  const sortedRegions = Object.keys(tunisianRegions).sort();

  const { register, handleSubmit, reset, formState: { errors }, watch, setValue, control, getValues } = useForm({
    mode: 'onBlur',
    defaultValues: {
      userType: 'individual',
      trade: [],
      registrationNumber: '',
      address: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      region: '',
      city: '',
      businessName: '',
      category: '',
      /** Une ligne par couple service / sous-services (chaque ligne : un sous-service affiché comme sur le formulaire). */
      tradeCategorySlots: [['']],
      experience: '',
      qualifications: '',
      description: '',
      pricing: '',
      serviceId: serviceId || '',
      schedule: '',
      scheduleNotes: ''
    }
  });

  /** Matrice locale : une ligne par bloc Service ; une colonne = sous-service (une liste par métier). */
  const [subServiceSlots, setSubServiceSlots] = useState([['']]);

  const cloneSlotMatrix = (m) =>
    Array.isArray(m) ? m.map((row) => (Array.isArray(row) ? [...row] : [''])) : [['']];

  const commitSlotMatrix = (next) => {
    const cloned = cloneSlotMatrix(next);
    setSubServiceSlots(cloned);
    setValue('tradeCategorySlots', cloned, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
  };

  const regionValue = watch('region');
  const passwordValue = watch('password');
  const tradeValue = watch('trade');
  const categoryValue = watch('category');
  const normalizeSelectedServices = (value) => {
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter(Boolean);
    }
    if (value == null) return [];
    const v = String(value).trim();
    return v ? [v] : [];
  };
  const tradeValueArray = Array.isArray(tradeValue)
    ? tradeValue
    : (tradeValue ? [tradeValue] : []);
  const tradeRows = tradeValueArray.length
    ? tradeValueArray.map((v) => (v == null ? '' : String(v).trim()))
    : [''];
  const slotsForRow = (rowIndex) => {
    const row = subServiceSlots[rowIndex];
    if (!Array.isArray(row) || row.length === 0) return [''];
    return row.map((v) => (v == null ? '' : String(v).trim()));
  };

  const serviceRowId = (s) => (s._id != null ? String(s._id) : String(s.title || ''));

  /** Résout le bon objet service (évite de prendre le mauvais via title ou le service URL). */
  const resolveServiceEntry = (tradeSelection) => {
    if (tradeSelection == null || tradeSelection === '') return null;
    const t = String(tradeSelection).trim();
    const byId = services.find((s) => s._id != null && String(s._id) === t);
    if (byId) return byId;
    const byTitle = services.find((s) => s.title != null && String(s.title) === t);
    if (byTitle) return byTitle;
    if (service && String(service._id) === t) return service;
    if (service && service.title != null && String(service.title) === t) return service;
    return null;
  };

  const tradeRowsFromForm = () => {
    const tv = getValues('trade');
    const tradeValueArray = Array.isArray(tv) ? tv : tv ? [tv] : [];
    return tradeValueArray.length
      ? tradeValueArray.map((v) => (v == null ? '' : String(v).trim()))
      : [''];
  };

  const updateTradeRow = (index, value) => {
    const normalized = value == null ? '' : String(value).trim();
    const rows = tradeRowsFromForm();
    const nextTrade = [...rows];
    while (nextTrade.length <= index) nextTrade.push('');
    nextTrade[index] = normalized;
    let nextSlots = cloneSlotMatrix(subServiceSlots);
    while (nextSlots.length < nextTrade.length) nextSlots.push(['']);
    nextSlots[index] = [''];
    setValue('trade', nextTrade, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    commitSlotMatrix(nextSlots);
    const firstPick = (nextSlots[0] || []).find((s) => s && String(s).trim());
    setValue('category', firstPick || '');
  };

  const setSubCategoryRow = (rowIndex, rawValue) => {
    const value = rawValue == null ? '' : String(rawValue).trim();
    const next = subServiceSlots.map((r, i) => {
      if (i !== rowIndex) return Array.isArray(r) ? [...r] : [''];
      return [value];
    });
    commitSlotMatrix(next);
    const firstPick = (next[0] || []).find((s) => s && String(s).trim());
    setValue('category', firstPick || '');
  };

  const addServiceRow = () => {
    const rows = tradeRowsFromForm();
    if (!services.length || rows.length >= services.length) return;
    const nextTrade = [...rows, ''];
    const nextSlots = [...subServiceSlots, ['']];
    setValue('trade', nextTrade, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    commitSlotMatrix(nextSlots);
  };

  const removeServiceRow = (index) => {
    if (index === 0) return;
    const rows = tradeRowsFromForm();
    const nextTrade = rows.filter((_, i) => i !== index);
    const nextSlots = subServiceSlots.filter((_, i) => i !== index);
    setValue('trade', nextTrade.length ? nextTrade : [''], { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    commitSlotMatrix(nextSlots.length ? nextSlots : [['']]);
    const firstPick = ((nextSlots[0] || []).find((s) => s && String(s).trim())) || '';
    setValue('category', firstPick);
  };

  const servicesOptionsForRow = (rowIndex) => {
    const rows = tradeRowsFromForm();
    return services.filter((s) => {
      const sid = serviceRowId(s);
      if (sid === (rows[rowIndex] || '')) return true;
      return !rows.some((tid, i) => i !== rowIndex && tid && tid === sid);
    });
  };

  const normalizeCategoryLabels = (arr) => {
    if (!Array.isArray(arr)) return [];
    const out = [];
    const seen = new Set();
    for (const item of arr) {
      const raw =
        item != null && typeof item === 'object' && item.name != null
          ? String(item.name)
          : String(item ?? '');
      const s = raw.trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  };

  const getServiceCategoriesForTrade = (tradeSelection) => {
    const foundService = resolveServiceEntry(tradeSelection);
    if (!foundService) return [];
    const fromCats = normalizeCategoryLabels(foundService.categories);
    if (fromCats.length) return fromCats;
    return normalizeCategoryLabels(foundService.tags);
  };

  const getServiceTitleForTrade = (tradeSelection) => {
    const found = resolveServiceEntry(tradeSelection);
    return found?.title || String(tradeSelection || '');
  };

  // Chaque sous-service doit rester valide pour le service de sa ligne.
  useEffect(() => {
    const nextMatrix = subServiceSlots.map((slotsRow, rowIndex) => {
      const tradeSelection = tradeRows[rowIndex];
      if (!tradeSelection || !Array.isArray(slotsRow)) return slotsRow;
      const available = getServiceCategoriesForTrade(tradeSelection);
      return slotsRow.map((cat) => {
        const c = cat == null ? '' : String(cat).trim();
        if (!c) return '';
        return available.includes(c) ? c : '';
      });
    });
    const changed = JSON.stringify(nextMatrix) !== JSON.stringify(subServiceSlots);
    if (changed) {
      commitSlotMatrix(nextMatrix);
      const firstPick = (nextMatrix[0] || []).find((s) => s && String(s).trim());
      setValue('category', firstPick || '');
    }
  }, [tradeRows, services, service, setValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const formatTimeForDisplay = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    return `${h}h${m !== '00' ? m : ''}`;
  };
  const buildScheduleString = () => {
    const lines = days.map(({ key, label }) => {
      const d = workSchedule[key];
      if (!d || !d.open) return `${label} : ${t('providerSignup.closed')}`;
      return `${label} : ${formatTimeForDisplay(d.start)} - ${formatTimeForDisplay(d.end)}`;
    });
    return lines.join('\n');
  };

  const updateWorkSchedule = (dayKey, field, value) => {
    setWorkSchedule((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value }
    }));
  };

  // Fetch services and pre-select when serviceId in URL
  useEffect(() => {
    const fetchServices = async () => {
      try {
        try {
          await api.initializeServices(false);
        } catch (e) {
          console.log('Init services:', e?.message || 'ok');
        }
        const response = await api.getServices({});
        if (response.success && response.data) {
          setServices(response.data || []);
          if (serviceId) {
            const decoded = decodeURIComponent(serviceId);
            const foundService = response.data.find(s => 
              s._id === decoded || 
              s.title === decoded ||
              (s.title && s.title.toLowerCase() === decoded.toLowerCase())
            );
            if (foundService) {
              setService(foundService);
              const val = foundService._id ? String(foundService._id) : foundService.title;
              setValue('serviceId', val);
              setValue('trade', [val]);
              commitSlotMatrix([['']]);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchServices();
  }, [serviceId, setValue]);

  // Update cities when region changes
  useEffect(() => {
    if (regionValue && tunisianRegions[regionValue]) {
      const sortedCities = [...tunisianRegions[regionValue]].sort();
      setAvailableCities(sortedCities);
      setSelectedRegion(regionValue);
      setValue('city', '');
    } else {
      setAvailableCities([]);
      setSelectedRegion('');
    }
  }, [regionValue, setValue]);

  // Password requirements (same as post-request)
  useEffect(() => {
    if (passwordValue) {
      setPasswordRequirements({
        lowercase: /[a-z]/.test(passwordValue),
        uppercase: /[A-Z]/.test(passwordValue),
        digit: /[0-9]/.test(passwordValue),
        length: passwordValue.length >= 12
      });
    } else {
      setPasswordRequirements({
        lowercase: false,
        uppercase: false,
        digit: false,
        length: false
      });
    }
  }, [passwordValue]);

  const onSubmit = async (data) => {
    setLoading(true);
    setShowError(false);
    setShowSuccess(false);
    setErrorMessage('');

    try {
      // Register as provider with role
      // Le prestataire est enregistré avec le service sélectionné (métier) pour apparaître dans ce service
      const selectedServiceIds = normalizeSelectedServices(data.trade || data.serviceId);
      const slotsMatrix = Array.isArray(data.tradeCategorySlots) ? data.tradeCategorySlots : [];
      const selectedTradeCategories = selectedServiceIds.map((_, i) => {
        const row = Array.isArray(slotsMatrix[i]) ? slotsMatrix[i].filter((v) => v != null && String(v).trim()) : [];
        return row.join(', ');
      });
      if (!selectedServiceIds.length) {
        setErrorMessage(t('providerSignup.errors.tradeRequiredFull'));
        setShowError(true);
        setLoading(false);
        return;
      }
      for (let i = 0; i < selectedServiceIds.length; i += 1) {
        const serviceCategoriesForRow = getServiceCategoriesForTrade(selectedServiceIds[i]);
        const rowSlots = Array.isArray(slotsMatrix[i])
          ? slotsMatrix[i].filter((v) => v != null && String(v).trim())
          : [];
        if (serviceCategoriesForRow.length && rowSlots.length === 0) {
          setErrorMessage(t('providerSignup.errors.subServiceRequired', { line: i + 1 }));
          setShowError(true);
          setLoading(false);
          return;
        }
      }
      const selectedServicePairs = selectedServiceIds.map((id, index) => ({
        serviceId: id,
        serviceCategory: selectedTradeCategories[index] || undefined
      }));
      let photoDataUrl = null;
      if (photoFile && photoFile instanceof File) {
        try {
          photoDataUrl = await readFileAsDataUrl(photoFile);
        } catch (e) {
          console.warn('Photo read failed', e);
        }
      }
      const verificationDocsData = [];
      for (const file of documentFiles) {
        if (file && file instanceof File) {
          try {
            verificationDocsData.push(await readFileAsDataUrl(file));
          } catch (e) {
            console.warn('Document read failed', e);
          }
        }
      }
      let cinPhotoDataUrl = null;
      if (cinPhotoFile && cinPhotoFile instanceof File) {
        try {
          cinPhotoDataUrl = await readFileAsDataUrl(cinPhotoFile);
        } catch (e) {
          console.warn('CIN photo read failed', e);
        }
      }

      const registrationData = {
        name: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone,
        password: data.password,
        role: 'provider',
        address: data.address,
        region: data.region,
        city: data.city,
        // Informations professionnelles
        businessName: data.businessName, // Métier / Service proposé
        serviceCategory: (selectedTradeCategories[0] || data.category) || undefined, // Catégorie du service principal
        serviceCategories: selectedServicePairs,
        experience: data.experience,
        qualifications: data.qualifications, // Compétences
        description: data.description, // Disponibilité et description du service
        pricing: data.pricing || undefined, // Tarification
        serviceId: selectedServiceIds[0],
        serviceIds: selectedServiceIds,
        photo: photoDataUrl || undefined,
        verificationDocuments: verificationDocsData.length ? verificationDocsData : undefined,
        cinPhoto: cinPhotoDataUrl || undefined,
        schedule: (() => {
          const fromGrid = buildScheduleString();
          const notes = data.scheduleNotes ? String(data.scheduleNotes).trim() : '';
          return fromGrid + (notes ? '\n' + notes : '') || undefined;
        })()
      };

      const response = await api.register(registrationData);

      if (response.success) {
        setShowSuccess(true);
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      } else {
        setErrorMessage(response.message || t('providerSignup.errors.registerFailed'));
        setShowError(true);
      }
    } catch (error) {
      setErrorMessage(error.message || t('providerSignup.errors.generic'));
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const toasterMessages = [];
  if (showError) {
    toasterMessages.push({ id: 'error', header: t('providerSignup.errorTitle'), body: errorMessage });
  }
  if (showSuccess) {
    toasterMessages.push({
      id: 'success',
      header: t('providerSignup.toastSuccess'),
      body: t('providerSignup.toastRedirect'),
    });
  }
  const toasterVariant = showSuccess ? 'success' : showError ? 'error' : null;

  return (
    <AppLayout title={t('providerSignup.pageTitle')} rootClass="layout-1">
      {toasterMessages.length > 0 && (
        <NioToaster 
          variant={toasterVariant} 
          messages={toasterMessages} 
        />
      )}
      
      <NioSection className="overflow-hidden pt-120 pt-lg-160" masks={["blur-1 left center"]}>
        <NioSection.Content>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <div className="text-center mb-5">
                <h2 className="mb-3">{t('providerSignup.heading')}</h2>
                <p className="fs-18 text-muted">{t('providerSignup.subtitle')}</p>
                {service && (
                  <div className="mt-4">
                    <NioBadge 
                      rounded 
                      className="text-bg-primary-soft mb-3" 
                      label={t('providerSignup.selectedService', { name: translateServiceLabel(t, service.title) })} 
                    />
                  </div>
                )}
              </div>

              {showSuccess && (
                <div className="alert alert-success mb-4 mb-md-5 d-flex align-items-center" role="alert">
                  <NioIcon name="check-circle-fill" className="fs-4 me-3 flex-shrink-0" />
                  <div>
                    <strong>{t('providerSignup.successTitle')}</strong>
                    <p className="mb-0 mt-1 small">{t('providerSignup.successRedirect')}</p>
                  </div>
                </div>
              )}
              {showError && (
                <div className="alert alert-danger mb-4 mb-md-5 d-flex align-items-center" role="alert">
                  <NioIcon name="alert-circle-fill" className="fs-4 me-3 flex-shrink-0" />
                  <div>
                    <strong>{t('providerSignup.errorTitle')}</strong>
                    <p className="mb-0 mt-1 small">{errorMessage}</p>
                  </div>
                </div>
              )}
              <NioCard>
                <NioCard.Body className="p-4 p-md-5">
                  <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                    {/* Détails du Service - same form as Post a Job */}
                    <h5 className="mb-4">{t('providerSignup.serviceDetails')}</h5>

                    {/* You are: Toggle */}
                    <div className="mb-4">
                      <label className="form-label fw-semibold mb-3">{t('providerSignup.youAre')}</label>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className={`btn flex-fill ${userType === 'company' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => {
                            setUserType('company');
                            setValue('userType', 'company');
                          }}
                        >
                          {t('providerSignup.company')}
                        </button>
                        <button
                          type="button"
                          className={`btn flex-fill ${userType === 'individual' ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => {
                            setUserType('individual');
                            setValue('userType', 'individual');
                          }}
                        >
                          {t('providerSignup.individual')}
                        </button>
                      </div>
                    </div>

                    {/* Services : blocs Service + Sous-service ; + AJOUTER = nouveau métier */}
                    <div className="mb-4">
                      <NioField label={t('providerSignup.tradeLabel')} htmlFor="trade-0">
                        <Controller
                          name="trade"
                          control={control}
                          rules={{
                            validate: (value) =>
                              normalizeSelectedServices(value).length > 0 ||
                              t('providerSignup.errors.tradeRequired')
                          }}
                          render={({ field: { value, onChange, ref, name, onBlur } }) => (
                            <select
                              ref={ref}
                              name={name}
                              onBlur={onBlur}
                              multiple
                              className="d-none"
                              aria-hidden="true"
                              tabIndex={-1}
                              value={normalizeSelectedServices(value)}
                              onChange={(e) => {
                                const vals = Array.from(e.target.selectedOptions, (opt) => opt.value);
                                onChange(vals);
                              }}
                            >
                              {normalizeSelectedServices(value).map((id) => (
                                <option key={id} value={id}>
                                  {id}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        {services.length > 0 ? (
                          <>
                            {tradeRows.map((tradeId, index) => {
                              const subCats = tradeId ? getServiceCategoriesForTrade(tradeId) : [];
                              const subVal = slotsForRow(index)[0] || '';
                              const rowSig = `${tradeId}|${subVal}|${index}`;
                              return (
                                <div
                                  key={`svc-block-${index}-${rowSig}`}
                                  className="p-3 mb-3 rounded border bg-light"
                                >
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="fw-semibold text-body small text-muted">
                                      {index === 0 ? t('providerSignup.mainService') : t('providerSignup.serviceN', { n: index + 1 })}
                                    </span>
                                    {index > 0 ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => removeServiceRow(index)}
                                      >
                                        {t('providerSignup.remove')}
                                      </button>
                                    ) : null}
                                  </div>
                                  <NioField label={t('providerSignup.service')} htmlFor={`trade-${index}`}>
                                    <NioField.Select
                                      key={`trade-sel-${index}-${tradeId || 'none'}`}
                                      id={`trade-${index}`}
                                      value={tradeId || ''}
                                      errors={index === 0 ? errors.trade : undefined}
                                      onChange={(e) => updateTradeRow(index, e.target.value)}
                                    >
                                      <option value="">{t('providerSignup.selectService')}</option>
                                      {servicesOptionsForRow(index).map((s) => {
                                        const sid = serviceRowId(s);
                                        return (
                                          <option key={sid} value={sid}>
                                            {translateServiceLabel(t, s.title)}
                                          </option>
                                        );
                                      })}
                                    </NioField.Select>
                                  </NioField>
                                  {tradeId && subCats.length > 0 && (
                                    <div className="mt-3">
                                      <NioField
                                        label={t('providerSignup.subService')}
                                        htmlFor={`tradeCat-${index}`}
                                      >
                                        <NioField.Select
                                          key={`trade-sub-${index}-${tradeId}`}
                                          id={`tradeCat-${index}`}
                                          value={subVal}
                                          onChange={(e) => setSubCategoryRow(index, e.target.value)}
                                        >
                                          <option value="">{t('providerSignup.selectSubService')}</option>
                                          {subCats.map((cat) => (
                                            <option key={cat} value={cat}>
                                              {translateServiceLabel(t, cat)}
                                            </option>
                                          ))}
                                        </NioField.Select>
                                      </NioField>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <div className="d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={addServiceRow}
                                disabled={tradeRows.length >= services.length}
                              >
                                {t('providerSignup.add')}
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="text-muted mb-0">{t('providerSignup.loadingServices')}</p>
                        )}
                      </NioField>
                      {errors.trade && (
                        <small className="text-danger d-block mt-2">{errors.trade.message}</small>
                      )}
                      <p className="text-muted small mb-0 mt-2">{t('providerSignup.tradeHint')}</p>
                    </div>

                    {/* UTR / Registration Number (if company) */}
                    {userType === 'company' && (
                      <div className="mb-4">
                        <NioField label={t('providerSignup.registrationNumber')} htmlFor="registrationNumber">
                          <NioField.Input
                            id="registrationNumber"
                            type="text"
                            placeholder={t('providerSignup.registrationPlaceholder')}
                            errors={errors.registrationNumber}
                            {...register('registrationNumber', {
                              required: userType === 'company' ? t('providerSignup.errors.registrationRequired') : false
                            })}
                          />
                        </NioField>
                      </div>
                    )}

                    {/* Photo, documents Vérifié, horaires - partie "Devenir prestataire" */}
                    <h5 className="mb-3 mt-5">{t('providerSignup.yourPhoto')}</h5>
                    <div className="mb-4">
                      <NioField label={t('providerSignup.profilePhoto')} htmlFor="photo">
                        <div className="d-flex align-items-start gap-3 flex-wrap">
                          {photoPreview && (
                            <div className="flex-shrink-0">
                              <img
                                src={photoPreview}
                                alt={t('providerSignup.photoPreview')}
                                className="rounded border"
                                style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              />
                            </div>
                          )}
                          <div className="flex-grow-1">
                            <input
                              id="photo"
                              type="file"
                              accept="image/*"
                              className="form-control"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                setPhotoFile(f || null);
                                setPhotoPreview(f ? URL.createObjectURL(f) : null);
                              }}
                            />
                            <small className="text-muted d-block mt-1">{t('providerSignup.photoHint')}</small>
                          </div>
                        </div>
                      </NioField>
                    </div>

                    <h5 className="mb-3 mt-4">{t('providerSignup.verifiedBadge')}</h5>
                    <p className="text-muted mb-3">{t('providerSignup.verifiedDesc')}</p>
                    <div className="mb-4">
                      <NioField label={t('providerSignup.documents')} htmlFor="verificationDocuments">
                        <input
                          id="verificationDocuments"
                          type="file"
                          accept=".pdf,image/*"
                          multiple
                          className="form-control"
                          onChange={(e) => {
                            const files = e.target.files ? Array.from(e.target.files) : [];
                            setDocumentFiles(files);
                          }}
                        />
                        {documentFiles.length > 0 && (
                          <small className="text-muted d-block mt-2">
                            {t('providerSignup.filesSelected', {
                              count: documentFiles.length,
                              names: documentFiles.map((f) => f.name).join(', '),
                            })}
                          </small>
                        )}
                      </NioField>
                    </div>

                    <div className="mb-4">
                      <NioField label={t('providerSignup.cinPhoto')} htmlFor="cinPhoto">
                        <input
                          id="cinPhoto"
                          type="file"
                          accept="image/*"
                          className="form-control"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            setCinPhotoFile(f || null);
                          }}
                        />
                        <small className="text-muted d-block mt-1">{t('providerSignup.cinHint')}</small>
                      </NioField>
                    </div>

                    <h5 className="mb-3 mt-4">{t('providerSignup.workSchedule')}</h5>
                    <p className="text-muted mb-3">{t('providerSignup.workScheduleDesc')}</p>
                    <div className="mb-4">
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t('providerSignup.day')}</th>
                              <th className="text-center" style={{ width: '100px' }}>{t('providerSignup.open')}</th>
                              <th>{t('providerSignup.from')}</th>
                              <th>{t('providerSignup.to')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {days.map(({ key, label }) => (
                              <tr key={key}>
                                <td className="fw-medium">{label}</td>
                                <td className="text-center">
                                  <Form.Check
                                    type="checkbox"
                                    checked={workSchedule[key]?.open || false}
                                    onChange={(e) => updateWorkSchedule(key, 'open', e.target.checked)}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="time"
                                    className="form-control form-control-sm"
                                    value={workSchedule[key]?.start || '09:00'}
                                    onChange={(e) => updateWorkSchedule(key, 'start', e.target.value)}
                                    disabled={!workSchedule[key]?.open}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="time"
                                    className="form-control form-control-sm"
                                    value={workSchedule[key]?.end || '18:00'}
                                    onChange={(e) => updateWorkSchedule(key, 'end', e.target.value)}
                                    disabled={!workSchedule[key]?.open}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <small className="text-muted d-block mt-2">{t('providerSignup.scheduleHint')}</small>
                      <NioField label={t('providerSignup.scheduleNotes')} htmlFor="scheduleNotes" className="mt-3">
                        <NioField.Textarea
                          id="scheduleNotes"
                          rows={2}
                          placeholder={t('providerSignup.scheduleNotesPlaceholder')}
                          errors={errors.scheduleNotes}
                          {...register('scheduleNotes')}
                        />
                      </NioField>
                    </div>

                    {/* Coordonnées et identité */}
                    <h5 className="mb-4 mt-5">{t('providerSignup.coordinates')}</h5>

                    {/* Address */}
                    <div className="mb-4">
                      <NioField label={t('providerSignup.address')} htmlFor="address">
                        <div className="position-relative">
                          <NioIcon name="location" className="position-absolute start-0 top-50 translate-middle-y ms-3 text-muted" />
                          <NioField.Input
                            id="address"
                            type="text"
                            placeholder={t('providerSignup.addressPlaceholder')}
                            className="ps-5"
                            errors={errors.address}
                            {...register('address')}
                          />
                        </div>
                      </NioField>
                    </div>

                    {/* Last Name and First Name */}
                    <Row className="g-3 mb-4">
                      <Col md={6}>
                        <NioField label={t('providerSignup.lastName')} htmlFor="lastName">
                          <NioField.Input
                            id="lastName"
                            type="text"
                            placeholder={t('providerSignup.lastNamePlaceholder')}
                            errors={errors.lastName}
                            {...register('lastName', {
                              required: t('providerSignup.errors.lastNameRequired'),
                              minLength: { value: 2, message: t('providerSignup.errors.lastNameMin') }
                            })}
                          />
                          <small className="text-muted d-block mt-1">{t('providerSignup.lastNameHidden')}</small>
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('providerSignup.firstName')} htmlFor="firstName">
                          <NioField.Input
                            id="firstName"
                            type="text"
                            placeholder={t('providerSignup.firstNamePlaceholder')}
                            errors={errors.firstName}
                            {...register('firstName', {
                              required: t('providerSignup.errors.firstNameRequired'),
                              minLength: { value: 2, message: t('providerSignup.errors.firstNameMin') }
                            })}
                          />
                        </NioField>
                      </Col>
                    </Row>

                    {/* Email and Mobile Phone */}
                    <Row className="g-3 mb-4">
                      <Col md={6}>
                        <NioField label={t('providerSignup.email')} htmlFor="email">
                          <NioField.Input
                            id="email"
                            type="email"
                            placeholder={t('providerSignup.emailPlaceholder')}
                            errors={errors.email}
                            {...register('email', {
                              required: t('providerSignup.errors.emailRequired'),
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: t('providerSignup.errors.emailInvalid')
                              }
                            })}
                          />
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('providerSignup.phone')} htmlFor="phone">
                          <NioField.Input
                            id="phone"
                            type="tel"
                            placeholder={t('providerSignup.phonePlaceholder')}
                            maxLength={8}
                            errors={errors.phone}
                            {...register('phone', {
                              required: t('providerSignup.errors.phoneRequired'),
                              maxLength: {
                                value: 8,
                                message: t('providerSignup.errors.phoneDigits')
                              },
                              pattern: {
                                value: /^[0-9]{8}$/,
                                message: t('providerSignup.errors.phoneDigits')
                              }
                            })}
                          />
                        </NioField>
                      </Col>
                    </Row>

                    {/* Region and City */}
                    <Row className="g-3 mb-4">
                      <Col md={6}>
                        <NioField label={t('providerSignup.region')} htmlFor="region">
                          <NioField.Select
                            id="region"
                            errors={errors.region}
                            {...register('region', { required: t('providerSignup.errors.regionRequired') })}
                          >
                            <option value="">{t('providerSignup.selectRegion')}</option>
                            {sortedRegions.map(region => (
                              <option key={region} value={region}>{region}</option>
                            ))}
                          </NioField.Select>
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('providerSignup.city')} htmlFor="city">
                          <NioField.Select
                            id="city"
                            errors={errors.city}
                            disabled={!selectedRegion}
                            {...register('city', { required: t('providerSignup.errors.cityRequired') })}
                          >
                            <option value="">{t('providerSignup.selectCity')}</option>
                            {availableCities.map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </NioField.Select>
                        </NioField>
                      </Col>
                    </Row>

                    {/* Informations professionnelles */}
                    <h5 className="mb-4 mt-5">{t('providerSignup.proInfo')}</h5>
                    <Row className="g-3 mb-4">
                      <Col md={12}>
                        <NioField label={t('providerSignup.businessName')} htmlFor="businessName">
                          <NioField.Input
                            id="businessName"
                            type="text"
                            placeholder={t('providerSignup.businessPlaceholder')}
                            errors={errors.businessName}
                            {...register('businessName', { required: t('providerSignup.errors.businessRequired') })}
                          />
                        </NioField>
                      </Col>
                      <Col md={6}>
                        <NioField label={t('providerSignup.experience')} htmlFor="experience">
                          <NioField.Select
                            id="experience"
                            errors={errors.experience}
                            {...register('experience', { required: t('providerSignup.errors.experienceRequired') })}
                          >
                            <option value="">{t('providerSignup.selectExperience')}</option>
                            <option value="0-1">{t('providerSignup.exp01')}</option>
                            <option value="2-5">{t('providerSignup.exp25')}</option>
                            <option value="6-10">{t('providerSignup.exp610')}</option>
                            <option value="11+">{t('providerSignup.exp11')}</option>
                          </NioField.Select>
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('providerSignup.description')} htmlFor="description">
                          <NioField.Textarea
                            id="description"
                            rows={4}
                            placeholder={t('providerSignup.descriptionPlaceholder')}
                            errors={errors.description}
                            {...register('description', {
                              required: t('providerSignup.errors.descriptionRequired'),
                              minLength: { value: 20, message: t('providerSignup.errors.descriptionMin') }
                            })}
                          />
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('providerSignup.qualifications')} htmlFor="qualifications">
                          <NioField.Textarea
                            id="qualifications"
                            rows={3}
                            placeholder={t('providerSignup.qualificationsPlaceholder')}
                            errors={errors.qualifications}
                            {...register('qualifications')}
                          />
                        </NioField>
                      </Col>
                      <Col md={12}>
                        <NioField label={t('providerSignup.pricing')} htmlFor="pricing">
                          <NioField.Input
                            id="pricing"
                            type="text"
                            placeholder={t('providerSignup.pricingPlaceholder')}
                            errors={errors.pricing}
                            {...register('pricing')}
                          />
                        </NioField>
                      </Col>
                    </Row>

                    {/* Account Information - same as Post a Job */}
                    <h5 className="mb-4 mt-5">{t('providerSignup.accountInfo')}</h5>
                    <div className="mb-4">
                      <NioField label={t('providerSignup.password')} htmlFor="password">
                        <div className="position-relative">
                          <NioField.Input
                            id="password"
                            type={passwordVisible ? 'text' : 'password'}
                            placeholder={t('providerSignup.passwordPlaceholder')}
                            errors={errors.password}
                            className="pe-5"
                            {...register('password', {
                              required: t('providerSignup.errors.passwordRequired'),
                              validate: (value) => {
                                const hasLower = /[a-z]/.test(value);
                                const hasUpper = /[A-Z]/.test(value);
                                const hasDigit = /[0-9]/.test(value);
                                const hasLength = value.length >= 12;
                                if (!hasLower || !hasUpper || !hasDigit || !hasLength) {
                                  return t('providerSignup.errors.passwordCriteria');
                                }
                                return true;
                              }
                            })}
                          />
                          <button
                            type="button"
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                            onClick={() => setPasswordVisible(!passwordVisible)}
                            style={{ border: 'none', background: 'none', padding: 0 }}
                          >
                            <NioIcon name={passwordVisible ? 'eye-off' : 'eye'} className="text-muted" />
                          </button>
                        </div>
                        <div className="mt-3">
                          <div className={`d-flex align-items-center mb-2 ${passwordRequirements.lowercase ? 'text-success' : 'text-danger'}`}>
                            {passwordRequirements.lowercase ? <NioIcon name="check" className="me-2" /> : <NioIcon name="cross" className="me-2" />}
                            <small>{t('providerSignup.pwdLower')}</small>
                          </div>
                          <div className={`d-flex align-items-center mb-2 ${passwordRequirements.uppercase ? 'text-success' : 'text-danger'}`}>
                            {passwordRequirements.uppercase ? <NioIcon name="check" className="me-2" /> : <NioIcon name="cross" className="me-2" />}
                            <small>{t('providerSignup.pwdUpper')}</small>
                          </div>
                          <div className={`d-flex align-items-center mb-2 ${passwordRequirements.digit ? 'text-success' : 'text-danger'}`}>
                            {passwordRequirements.digit ? <NioIcon name="check" className="me-2" /> : <NioIcon name="cross" className="me-2" />}
                            <small>{t('providerSignup.pwdDigit')}</small>
                          </div>
                          <div className={`d-flex align-items-center mb-2 ${passwordRequirements.length ? 'text-success' : 'text-danger'}`}>
                            {passwordRequirements.length ? <NioIcon name="check" className="me-2" /> : <NioIcon name="cross" className="me-2" />}
                            <small>{t('providerSignup.pwdLength')}</small>
                          </div>
                        </div>
                      </NioField>
                    </div>
                    <div className="mb-4">
                      <NioField label={t('providerSignup.confirmPassword')} htmlFor="confirmPassword">
                        <NioField.Input
                          id="confirmPassword"
                          type="password"
                          placeholder={t('providerSignup.confirmPasswordPlaceholder')}
                          icon="eye-fill after text-primary"
                          errors={errors.confirmPassword}
                          {...register('confirmPassword', {
                            required: t('providerSignup.errors.confirmRequired'),
                            validate: (value) => value === watch('password') || t('providerSignup.errors.confirmMismatch')
                          })}
                        />
                      </NioField>
                    </div>

                    {/* Submit Button */}
                    <div className="d-flex justify-content-between align-items-center mt-5">
                      <Link to="/services" className="text-muted">
                        {t('providerSignup.backToServices')}
                      </Link>
                      <NioButton
                        type="submit"
                        className="btn-lg btn-primary"
                        label={loading ? t('providerSignup.submitting') : t('providerSignup.submit')}
                        disabled={loading}
                      />
                    </div>
                  </Form>
                </NioCard.Body>
              </NioCard>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}
